import { CalendarClock, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";

const Eyebrow = ({ children, color }) => (
  <div className="font-mono-meta text-[11px] tracking-[0.24em] uppercase" style={{ color }}>{children}</div>
);

export const EditionCard = ({ p, theme, ensureSaved, onStarted }) => {
  const job = p.update_job || {};
  const running = job.status === "running";
  const overdue = p.next_update_due && new Date(p.next_update_due) < new Date();
  const soon = p.next_update_due && new Date(p.next_update_due) - new Date() < 14 * 864e5;

  const update = async () => {
    if (!window.confirm("Re-check every chapter for accuracy and publish a new edition? Your current edition stays downloadable until the new one is ready.")) return;
    try {
      await ensureSaved();
      await api.post(`/products/${p.id}/update-edition`);
      toast.success("Updating — this takes a minute per chapter");
      onStarted();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const testReminder = async () => {
    try {
      const r = await api.post(`/products/${p.id}/test-reminder`);
      toast.success(`Test reminder sent to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pastBuyers = async () => {
    if (!window.confirm("Email every past buyer of this title a fresh download link for the current edition?")) return;
    try {
      const r = await api.post(`/products/${p.id}/email-past-buyers`);
      toast.success(`Sent to ${r.data.sent} of ${r.data.buyers} past buyers`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="grid lg:grid-cols-12 border border-[#2A2325]/10 bg-[#FFFDF9]" data-testid="edition-card">
      <div className="lg:col-span-6 p-6 md:p-7 bg-[#F7F3EC] border-b lg:border-b-0 lg:border-r border-[#2A2325]/10">
        <div className="flex items-center justify-between gap-3">
          <Eyebrow color={theme.accent}><span className="inline-flex items-center gap-2"><CalendarClock size={14} /> Accuracy · 6-month update cycle</span></Eyebrow>
          <span className="font-mono-meta text-[10px] tracking-[0.18em] px-2 py-1 border" style={{ borderColor: overdue ? "#945B17" : "#2D5A3F55", color: overdue ? "#945B17" : "#2D5A3F" }} data-testid="edition-status">
            {running ? "UPDATING" : overdue ? "UPDATE DUE" : soon ? "DUE SOON" : "UP TO DATE"}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
          <div><div className="text-[#5C5255] text-xs">Edition</div><div className="font-playfair text-2xl mt-1" data-testid="edition-number">{p.edition || 1}</div></div>
          <div><div className="text-[#5C5255] text-xs">Last updated</div><div className="mt-2">{fmtDate(p.edition_updated_at)}</div></div>
          <div><div className="text-[#5C5255] text-xs">Next update due</div><div className="mt-2" data-testid="edition-next-due">{fmtDate(p.next_update_due)}</div></div>
        </div>
        <Button onClick={update} disabled={running} className="rounded-none gap-2 mt-6" style={{ background: theme.dark }} data-testid="update-edition-btn">
          <RefreshCw size={15} className={running ? "animate-spin" : ""} /> {running ? `Updating ${(job.progress || 0) + 1} of ${job.total}…` : `Update ${p.product_type === "workbook" ? "workbook" : p.product_type === "book" ? "book" : "guide"} now`}
        </Button>
        {running && job.message && <div className="text-xs text-[#5C5255] mt-2 italic" data-testid="edition-progress">Reviewing “{job.message}”</div>}
        {job.status === "failed" && <div className="text-xs text-[#9B2C2C] mt-2" data-testid="edition-error">Last update failed: {job.error}</div>}
        <p className="text-xs text-[#5C5255] mt-3 leading-relaxed">Re-checks every chapter, rewrites what's out of date, re-typesets the PDF, sample and toolkit, and logs what changed. Your store details, issue number and publish status are kept; the current edition stays downloadable until the new one is ready.</p>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#2A2325]/10 mt-5 pt-4 text-xs text-[#5C5255]">
          <span className="flex items-center gap-2"><Mail size={13} style={{ color: theme.accent }} /> Email reminders: 2 weeks before and on the due date</span>
          <span className="flex gap-4">
            <button onClick={testReminder} className="text-[#2A2325] hover:underline" data-testid="test-reminder-btn">Send test reminder</button>
            <button onClick={pastBuyers} className="text-[#2A2325] hover:underline" data-testid="email-past-buyers-btn">Email past buyers</button>
          </span>
        </div>
      </div>
      <div className="lg:col-span-6 p-6 md:p-7">
        <Eyebrow color={theme.accent}>Edition history</Eyebrow>
        <ul className="mt-5 space-y-4 max-h-72 overflow-y-auto" data-testid="edition-history">
          {[...(p.edition_history || [])].reverse().map((h) => (
            <li key={h.edition} className="border-l-2 pl-4" style={{ borderColor: theme.accent }}>
              <div className="flex items-baseline gap-2"><span className="font-playfair text-lg">Edition {h.edition}</span><span className="text-xs text-[#5C5255]">{fmtDate(h.date)} · {h.notes?.length || 0} {h.notes?.length === 1 ? "note" : "notes"}</span></div>
              <ul className="text-xs text-[#5C5255] mt-1 space-y-0.5">{(h.notes || []).map((n, i) => <li key={i}>— {n}</li>)}</ul>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
